import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Button } from 'react-bootstrap';
import dayjs from 'dayjs';

import useDownloadFiles from 'lib/hooks/useDownloadFiles';
import useGet from 'lib/hooks/useGet';
import SwalDialog from 'lib/utils/toast';
import { DEVIATED_PRICE_ANALYSIS_UPLOAD_FILE, getURL } from 'lib/networking/endpoints';

const STATUSES = {
  UPLOADED: 'uploaded',
  GENERATING: 'generating',
  DELETING: 'deleting',
  COMPLETED: 'completed',
  COMPLETED_NO_CONTRACT: 'completed_no_contract',
  FAILED: 'failed',
};

function DeviatedPriceAnalysisUploadRow({ upload, onDelete, onGenerateReport, isGenerating }) {
  const status = upload.status || '';
  const isProcessing =
    status === STATUSES.GENERATING || status === STATUSES.DELETING || isGenerating;
  const isGenerated = status === STATUSES.COMPLETED || status === STATUSES.COMPLETED_NO_CONTRACT;
  const canGenerate =
    status === STATUSES.UPLOADED ||
    status === STATUSES.FAILED ||
    status === STATUSES.COMPLETED ||
    status === STATUSES.COMPLETED_NO_CONTRACT;

  let actionButtonLabel = 'Generate Report';
  if (status === STATUSES.DELETING) actionButtonLabel = 'Deleting…';
  else if (isProcessing) actionButtonLabel = 'Generating Report…';
  else if (isGenerated) actionButtonLabel = 'Report Generated';

  const reportUrl = getURL(DEVIATED_PRICE_ANALYSIS_UPLOAD_FILE, { upload_id: upload.id });
  const { error: reportUrlError, refetch: fetchReportUrl } = useGet(reportUrl, {}, false, true);
  const { downloadFiles } = useDownloadFiles();

  useEffect(() => {
    if (reportUrlError) {
      const msg = reportUrlError.response?.data?.message || reportUrlError.message;
      SwalDialog('error', `Download failed: ${msg}`, 'Error');
    }
  }, [reportUrlError]);

  const handleDownloadReport = () => {
    fetchReportUrl(res => {
      const presignedUrl = res.data?.data;
      if (!presignedUrl) {
        SwalDialog('error', 'No download URL returned.', 'Error');
        return;
      }
      downloadFiles([presignedUrl]);
    });
  };

  return (
    <tr>
      <td className="py-3">{upload.id}</td>
      <td className="py-3">{upload.file_name}</td>
      <td className="py-3">
        <Button
          variant="outline-primary"
          size="sm"
          onClick={onGenerateReport}
          disabled={isProcessing || !canGenerate}
        >
          {actionButtonLabel}
        </Button>
      </td>
      <td className="py-3">
        {isGenerated && (
          <Button variant="outline-success" size="sm" onClick={handleDownloadReport}>
            Report CSV
          </Button>
        )}
      </td>
      <td className="py-3">
        {upload.status}
        {status === STATUSES.FAILED && upload.error_message && (
          <div className="text-danger small mt-1">{upload.error_message}</div>
        )}
      </td>
      <td className="py-3">
        {upload.date_created ? dayjs(upload.date_created).format('MM/DD/YY HH:mm') : '-'}
      </td>
      <td className="py-3">{upload.uploaded_by || '-'}</td>
      <td className="py-3">
        <Button variant="outline-danger" size="sm" onClick={onDelete} disabled={isProcessing}>
          Remove
        </Button>
      </td>
    </tr>
  );
}

DeviatedPriceAnalysisUploadRow.propTypes = {
  isGenerating: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onGenerateReport: PropTypes.func.isRequired,
  upload: PropTypes.object.isRequired,
};

DeviatedPriceAnalysisUploadRow.defaultProps = {
  isGenerating: false,
};

export default DeviatedPriceAnalysisUploadRow;
